import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PageTitle from "../../layouts/PageTitle";
import { Row, Col, Card, Table, Badge, Dropdown, Modal } from "react-bootstrap";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import AppPagination from "../Common/AppPagination";

const TeamMobilization = () => {
  const [teams, setTeams] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // View Mode: "user" (default User-Wise) or "mapping" (Role Mapping Wise)
  const [viewMode, setViewMode] = useState("user");
  const [expandedUsers, setExpandedUsers] = useState({});

  // Pagination and filter states
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasPrevPage: false,
    hasNextPage: false,
  });

  const [filters, setFilters] = usePersistentFilters("teams_filters", {
    search: "",
    status: "",
    role: "",
    page: 1,
    limit: 10,
  });

  const indexOfFirstItem = (pagination.currentPage - 1) * filters.limit;

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > pagination.totalPages) return;
    setFilters((prev) => ({ ...prev, page: pageNumber }));
  };

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const roles = [
    "Supervisor",
    "QC",
    "Safety Officer",
    "Site Engineer",
    "Project Manager",
    "Other",
  ];

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case "Project Manager":
        return "primary";
      case "Site Engineer":
        return "info";
      case "Supervisor":
        return "success";
      case "Safety Officer":
        return "warning text-dark";
      case "QC":
        return "secondary";
      default:
        return "dark";
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);

  const [newTeam, setNewTeam] = useState({
    engineers: [],
    role: "",
    assignedWorkOrders: [],
    assignedProjects: [],
    status: "Active",
  });

  const [editTeam, setEditTeam] = useState(null);
  const [editTeamId, setEditTeamId] = useState(null);

  // FILTERED OPTIONS
  const filteredWorkOrderOptions = workOrders
    .filter((wo) => {
      // If adding new team, filter by newTeam.assignedProjects
      if (showModal) {
        if (newTeam.assignedProjects.length === 0) return true;
        return newTeam.assignedProjects.includes(wo.project?._id || wo.project);
      }
      // If editing team, filter by editTeam.assignedProjects
      if (editModal && editTeam) {
        if (editTeam.assignedProjects.length === 0) return true;
        return editTeam.assignedProjects.includes(wo.project?._id || wo.project);
      }
      return true;
    })
    .map((wo) => ({
      value: wo._id,
      label: `${wo.woNo} - ${wo.project?.projectName || "Unnamed Project"}`,
    }));

  const renderEngineers = (engineers = []) => {
    if (!engineers.length)
      return <span className="text-muted small">No Users</span>;
    if (engineers.length === 1)
      return <span className="fw-semibold">{engineers[0].name || engineers[0].email}</span>;

    const [first, ...rest] = engineers;
    return (
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          variant=""
          size="sm"
          className="btn btn-sm btn-outline-primary py-1 px-2 text-nowrap d-inline-flex align-items-center gap-1 shadow-none"
          style={{ fontSize: "12px", borderRadius: "6px" }}
        >
          <span>{first.name || first.email}</span>
          <Badge bg="primary" className="ms-1" style={{ fontSize: "10px", padding: "2px 5px" }}>
            +{rest.length}
          </Badge>
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {engineers.map((e) => (
            <Dropdown.Item key={e._id}>{e.name || e.email}</Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderWorkOrders = (workOrders = []) => {
    if (!workOrders.length)
      return <span className="text-muted small">No Work Orders</span>;

    if (workOrders.length === 1)
      return <span className="fw-semibold text-primary">{workOrders[0].woNo}</span>;

    const [first, ...rest] = workOrders;

    return (
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          variant=""
          size="sm"
          className="btn btn-sm btn-outline-primary py-1 px-2 text-nowrap d-inline-flex align-items-center gap-1 shadow-none"
          style={{ fontSize: "12px", borderRadius: "6px" }}
        >
          <span className="fw-semibold">{first.woNo}</span>
          <Badge bg="primary" className="ms-1" style={{ fontSize: "10px", padding: "2px 5px" }}>
            +{rest.length}
          </Badge>
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {workOrders.map((wo) => (
            <Dropdown.Item key={wo._id || wo.woNo}>{wo.woNo}</Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderProjects = (projects = []) => {
    if (!projects.length)
      return <span className="text-muted small">No Projects</span>;

    if (projects.length === 1)
      return <span className="fw-semibold">{projects[0].projectName || projects[0].projectCode}</span>;

    const [first, ...rest] = projects;

    return (
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          variant=""
          size="sm"
          className="btn btn-sm btn-outline-primary py-1 px-2 text-nowrap d-inline-flex align-items-center gap-1 shadow-none"
          style={{ fontSize: "12px", borderRadius: "6px" }}
        >
          <span>{first.projectName || first.projectCode}</span>
          <Badge bg="primary" className="ms-1" style={{ fontSize: "10px", padding: "2px 5px" }}>
            +{rest.length}
          </Badge>
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {projects.map((p) => (
            <Dropdown.Item key={p._id || p.projectCode || p.projectName}>
              {p.projectName || p.projectCode}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  useEffect(() => {
    fetchEngineers();
    fetchWorkOrders();
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [filters, viewMode]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.role) queryParams.append("role", filters.role);
      queryParams.append("groupBy", viewMode);
      queryParams.append("page", filters.page);
      queryParams.append("limit", filters.limit);

      const res = await api.get(`/team?${queryParams.toString()}`);
      if (res.data.success) {
        setTeams(res.data.data || []);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      } else {
        swal("Error", res.data.message, "error");
      }
    } catch (err) {
      swal("Fetch Failed", "Unable to load teams", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teamId) => {
    const confirm = await swal({
      title: "Delete Team?",
      text: "This action cannot be undone",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirm) return;

    try {
      const res = await api.delete(`/team/${teamId}`);
      if (res.data.success) {
        swal("Deleted", "Team removed successfully", "success");
        fetchTeams();
      }
    } catch (err) {
      swal("Error", err.message, "error");
    }
  };

  const fetchEngineers = async () => {
    try {
      const res = await api.get("/users?limit=1000");
      if (res.data.success) setEngineers(res.data.data);
    } catch {}
  };

  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders?limit=1000");
      if (res.data.success) setWorkOrders(res.data.data);
    } catch {}
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get("/projects?limit=1000");
      if (res.data.success) setProjects(res.data.data);
    } catch {}
  };

  const engineerOptions = engineers.map((eng) => ({
    value: eng._id,
    label: eng.name || eng.email,
  }));

  const workOrderOptions = workOrders.map((wo) => ({
    value: wo._id,
    label: `${wo.woNo} - ${wo.project?.projectName || "Unnamed Project"}`,
  }));

  const projectOptions = projects.map((p) => ({
    value: p._id,
    label: p.projectName || p.projectCode || "Unnamed Project",
  }));

  const handleEngineerChange = (selected) => {
    setNewTeam({
      ...newTeam,
      engineers: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleWorkOrderChange = (selected) => {
    setNewTeam({
      ...newTeam,
      assignedWorkOrders: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleProjectChange = (selected) => {
    setNewTeam({
      ...newTeam,
      assignedProjects: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleEditEngineerChange = (selected) => {
    setEditTeam({
      ...editTeam,
      engineers: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleEditWorkOrderChange = (selected) => {
    setEditTeam({
      ...editTeam,
      assignedWorkOrders: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleEditProjectChange = (selected) => {
    setEditTeam({
      ...editTeam,
      assignedProjects: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleChange = (e, edit = false) => {
    const { name, value } = e.target;
    edit
      ? setEditTeam({ ...editTeam, [name]: value })
      : setNewTeam({ ...newTeam, [name]: value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!newTeam.engineers.length || !newTeam.role) {
      swal("Validation Error", "Engineer and Role are required", "warning");
      return;
    }

    const confirm = await swal({
      title: "Confirm Team Mapping",
      text: "Are you sure you want to map this role?",
      icon: "info",
      buttons: ["Cancel", "Map Role"],
    });
    if (!confirm) return;

    try {
      const payload = {
        ...newTeam,
        engineers: JSON.stringify(newTeam.engineers),
        assignedWorkOrders: JSON.stringify(newTeam.assignedWorkOrders),
        assignedProjects: JSON.stringify(newTeam.assignedProjects),
      };

      const res = await api.post("/team", payload);
      if (res.data.success) {
        swal("Success", "Team mapped successfully", "success");
        setShowModal(false);
        setNewTeam({
          engineers: [],
          role: "",
          assignedWorkOrders: [],
          assignedProjects: [],
          status: "Active",
        });

        fetchTeams();
      } else swal("Error", res.data.message, "error");
    } catch (err) {
      swal("Server Error", err.message, "error");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTeam.engineers.length || !editTeam.role) {
      swal("Validation Error", "Engineer and Role are required", "warning");
      return;
    }

    const confirm = await swal({
      title: "Update Team?",
      text: "Do you want to save these changes?",
      icon: "info",
      buttons: ["Cancel", "Update"],
    });
    if (!confirm) return;

    try {
      const payload = {
        ...editTeam,
        engineers: JSON.stringify(editTeam.engineers),
        assignedWorkOrders: JSON.stringify(editTeam.assignedWorkOrders),
        assignedProjects: JSON.stringify(editTeam.assignedProjects),
      };

      const res = await api.patch(`/team/${editTeamId}`, payload);
      if (res.data.success) {
        swal("Updated", "Team updated successfully", "success");
        setEditModal(false);
        fetchTeams();
      } else swal("Error", res.data.message, "error");
    } catch (err) {
      swal("Server Error", err.message, "error");
    }
  };

  const handleEditClick = (team) => {
    setEditTeamId(team._id);
    setEditTeam({
      engineers: team.engineers?.map((e) => e._id || e) || [],
      assignedWorkOrders: team.assignedWorkOrders?.map((w) => w._id || w) || [],
      assignedProjects: team.assignedProjects?.map((p) => p._id || p) || [],
      role: team.role,
      status: team.status,
    });

    setEditModal(true);
  };

  const svgMenu = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <circle fill="currentColor" cx="5" cy="12" r="2"></circle>
      <circle fill="currentColor" cx="12" cy="12" r="2"></circle>
      <circle fill="currentColor" cx="19" cy="12" r="2"></circle>
    </svg>
  );

  return (
    <Fragment>
      <PageTitle activeMenu="Team Mobilization" motherMenu="Teams" />

      {/* FILTER CARD */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by user, role, WO..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, search: e.target.value }))
                    }
                  />
                </Col>

                <Col md={2}>
                  <label className="form-label">Role</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.role}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, role: e.target.value, page: 1 }))
                    }
                  >
                    <option value="">All Roles</option>
                    {roles.map((r, i) => (
                      <option key={i} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col md={2}>
                  <label className="form-label">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))
                    }
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </Col>

                <Col md={2}>
                  <label className="form-label">Per Page</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.limit}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        limit: parseInt(e.target.value, 10),
                        page: 1,
                      }))
                    }
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </Col>

                <Col md={3} className="d-flex align-items-end">
                  <button className="btn btn-primary btn-sm w-100" onClick={handleSearch}>
                    <i className="fas fa-search me-2"></i>
                    Search
                  </button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* TEAMS LIST */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Card.Title className="mb-0">Team Mobilization & Role Mapping</Card.Title>
                
                {/* View Mode Toggle Pill Switch */}
                <div
                  className="d-inline-flex align-items-center p-1 border rounded-pill"
                  style={{
                    backgroundColor: "rgba(125, 125, 125, 0.12)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("user")}
                    className={`btn btn-sm rounded-pill py-1 px-3 d-inline-flex align-items-center gap-1 ${
                      viewMode === "user" ? "btn-primary shadow-sm" : "btn-link text-decoration-none text-muted"
                    }`}
                    style={{ fontSize: "12px", fontWeight: "600" }}
                  >
                    <i className="fas fa-users"></i>
                    <span>User-Wise View</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("mapping")}
                    className={`btn btn-sm rounded-pill py-1 px-3 d-inline-flex align-items-center gap-1 ${
                      viewMode === "mapping" ? "btn-primary shadow-sm" : "btn-link text-decoration-none text-muted"
                    }`}
                    style={{ fontSize: "12px", fontWeight: "600" }}
                  >
                    <i className="fas fa-layer-group"></i>
                    <span>Role Mapping View</span>
                  </button>
                </div>
              </div>

              <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-1"></i> Map Role
              </button>
            </Card.Header>

            <Card.Body>
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : teams.length === 0 ? (
                <div className="text-center alert alert-info">No team records found.</div>
              ) : viewMode === "user" ? (
                /* ================= USER-WISE VIEW TABLE ================= */
                <Table responsive className="table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "50px" }}>#</th>
                      <th>User Details</th>
                      <th>Assigned Role(s)</th>
                      <th>Total Assigned Projects</th>
                      <th>Total Assigned Work Orders</th>
                      <th>Status</th>
                      <th style={{ width: "130px" }} className="text-center">Role Details</th>
                    </tr>
                  </thead>

                  <tbody>
                    {teams.map((item, i) => {
                      const u = item.user || {};
                      const isExpanded = !!expandedUsers[item._id];
                      const recordCount = item.teamRecords?.length || 0;

                      return (
                        <Fragment key={item._id || i}>
                          <tr style={{ backgroundColor: isExpanded ? "rgba(13, 110, 253, 0.08)" : "inherit" }}>
                            <td>{indexOfFirstItem + i + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2 fw-bold"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    fontSize: "14px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {(u.name || u.email || "U").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="fw-bold">{u.name || "Unnamed User"}</div>
                                  <div className="small text-muted">{u.email || "-"}</div>
                                  {u.mobile && (
                                    <div className="small text-secondary">
                                      <i className="fas fa-phone-alt me-1" style={{ fontSize: "10px" }}></i>
                                      {u.mobile}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              {(item.roles || []).length > 0 ? (
                                item.roles.map((r, idx) => (
                                  <Badge
                                    key={idx}
                                    bg={getRoleBadgeVariant(r)}
                                    className="me-1 mb-1 px-2 py-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {r}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted small">No Roles</span>
                              )}
                            </td>
                            <td>{renderProjects(item.assignedProjects)}</td>
                            <td>{renderWorkOrders(item.assignedWorkOrders)}</td>
                            <td>
                              <Badge
                                bg={item.status === "Active" ? "success" : "danger"}
                                className="rounded-pill px-2 py-1"
                              >
                                {item.status}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className={`btn btn-sm ${isExpanded ? "btn-primary" : "btn-outline-primary"} py-1 px-2`}
                                onClick={() => toggleUserExpand(item._id)}
                                title="Click to view/edit individual role mappings"
                              >
                                <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} me-1`}></i>
                                {recordCount} {recordCount === 1 ? "Mapping" : "Mappings"}
                              </button>
                            </td>
                          </tr>

                          {/* EXPANDABLE SUB-TABLE OF ROLE MAPPINGS */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} className="p-0 border-0">
                                <div className="p-3 border-bottom" style={{ backgroundColor: "rgba(13, 110, 253, 0.03)" }}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong className="text-primary small">
                                      <i className="fas fa-sitemap me-1"></i> Role Breakdown for {u.name || u.email}
                                    </strong>
                                  </div>
                                  <div className="table-responsive rounded border shadow-sm">
                                    <Table size="sm" className="mb-0">
                                      <thead className="table-light">
                                        <tr>
                                          <th style={{ width: "40px" }}>#</th>
                                          <th>Role</th>
                                          <th>Assigned Projects for this Role</th>
                                          <th>Assigned Work Orders for this Role</th>
                                          <th>Status</th>
                                          <th className="text-end" style={{ width: "90px" }}>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(item.teamRecords || []).map((tr, trIdx) => (
                                          <tr key={tr._id || trIdx}>
                                            <td>{trIdx + 1}</td>
                                            <td>
                                              <Badge bg={getRoleBadgeVariant(tr.role)} className="px-2 py-1">
                                                {tr.role}
                                              </Badge>
                                            </td>
                                            <td>{renderProjects(tr.assignedProjects)}</td>
                                            <td>{renderWorkOrders(tr.assignedWorkOrders)}</td>
                                            <td>
                                              <Badge
                                                bg={tr.status === "Active" ? "success" : "danger"}
                                                className="rounded-pill"
                                              >
                                                {tr.status}
                                              </Badge>
                                            </td>
                                            <td className="text-end" style={{ width: "90px", whiteSpace: "nowrap" }}>
                                              <div className="d-flex justify-content-end align-items-center">
                                                <button
                                                  type="button"
                                                  className="btn btn-primary btn-sm me-1 d-inline-flex align-items-center justify-content-center"
                                                  style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    minWidth: "28px",
                                                    minHeight: "28px",
                                                    padding: 0,
                                                    borderRadius: "6px",
                                                  }}
                                                  onClick={() =>
                                                    handleEditClick({
                                                      _id: tr._id,
                                                      engineers: [u],
                                                      assignedProjects: tr.assignedProjects,
                                                      assignedWorkOrders: tr.assignedWorkOrders,
                                                      role: tr.role,
                                                      status: tr.status,
                                                    })
                                                  }
                                                  title="Edit this role mapping"
                                                >
                                                  <i className="fas fa-pencil-alt" style={{ fontSize: "12px", lineHeight: 1 }}></i>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                                                  style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    minWidth: "28px",
                                                    minHeight: "28px",
                                                    padding: 0,
                                                    borderRadius: "6px",
                                                  }}
                                                  onClick={() => handleDelete(tr._id)}
                                                  title="Delete this role mapping"
                                                >
                                                  <i className="fa fa-trash" style={{ fontSize: "12px", lineHeight: 1 }}></i>
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                /* ================= ROLE MAPPING VIEW TABLE ================= */
                <Table responsive className="table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "50px" }}>#</th>
                      <th>User(s)</th>
                      <th>Role</th>
                      <th>Assigned Projects</th>
                      <th>Assigned Work Orders</th>
                      <th>Status</th>
                      <th style={{ width: "60px" }}></th>
                    </tr>
                  </thead>

                  <tbody>
                    {teams.map((t, i) => (
                      <tr key={t._id}>
                        <td>{indexOfFirstItem + i + 1}</td>
                        <td>{renderEngineers(t.engineers)}</td>
                        <td>
                          <Badge bg={getRoleBadgeVariant(t.role)} className="px-2 py-1">
                            {t.role}
                          </Badge>
                        </td>
                        <td>{renderProjects(t.assignedProjects)}</td>
                        <td>{renderWorkOrders(t.assignedWorkOrders)}</td>

                        <td>
                          <Badge
                            bg={t.status === "Active" ? "success" : "danger"}
                            className="rounded-pill"
                          >
                            {t.status}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="" className="btn-link i-false">
                              {svgMenu}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEditClick(t)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDelete(t._id)}>
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <AppPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                itemsLength={teams.length}
                limit={filters.limit}
                onPageChange={handlePageChange}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ADD MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <form onSubmit={handleAddSubmit}>
          <div className="modal-header">
            <h4>Map Team Role</h4>
            <button className="btn-close" onClick={() => setShowModal(false)} />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label>User(s) *</label>
              <Select
                isMulti
                options={engineerOptions}
                onChange={handleEngineerChange}
                value={engineerOptions.filter((opt) =>
                  newTeam.engineers.includes(opt.value),
                )}
              />
            </div>

            <div className="mb-3">
              <label>Assigned Project(s) (Optional)</label>
              <Select
                isMulti
                options={projectOptions}
                value={projectOptions.filter((opt) =>
                  newTeam.assignedProjects.includes(opt.value),
                )}
                onChange={handleProjectChange}
              />
            </div>

            <div className="mb-3">
              <label>Assigned Work Order(s)</label>
              <Select
                isMulti
                options={filteredWorkOrderOptions}
                value={filteredWorkOrderOptions.filter((opt) =>
                  newTeam.assignedWorkOrders.includes(opt.value),
                )}
                onChange={handleWorkOrderChange}
                placeholder={
                  newTeam.assignedProjects.length > 0
                    ? "Select Work Orders for this project"
                    : "Select Project first"
                }
              />
            </div>

            <div className="mb-3">
              <label>Role *</label>
              <select
                name="role"
                value={newTeam.role}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Role</option>
                {roles.map((r, idx) => (
                  <option key={idx} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label>Status</label>
              <select
                name="status"
                className="form-select"
                value={newTeam.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-primary" type="submit">
              Save
            </button>
            <button
              className="btn btn-danger"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal show={editModal} onHide={() => setEditModal(false)}>
        <form onSubmit={handleEditSubmit}>
          <div className="modal-header">
            <h4>Edit Team Role</h4>
            <button className="btn-close" onClick={() => setEditModal(false)} />
          </div>
          <div className="modal-body">
            {editTeam && (
              <>
                <div className="mb-3">
                  <label>User(s) *</label>
                  <Select
                    isMulti
                    options={engineerOptions}
                    value={engineerOptions.filter((opt) =>
                      editTeam.engineers.includes(opt.value),
                    )}
                    onChange={handleEditEngineerChange}
                  />
                </div>

                <div className="mb-3">
                  <label>Assigned Project(s) (Optional)</label>
                  <Select
                    isMulti
                    options={projectOptions}
                    value={projectOptions.filter((opt) =>
                      editTeam.assignedProjects.includes(opt.value),
                    )}
                    onChange={handleEditProjectChange}
                  />
                </div>

                <div className="mb-3">
                  <label>Assigned Work Order(s)</label>
                  <Select
                    isMulti
                    options={filteredWorkOrderOptions}
                    value={filteredWorkOrderOptions.filter((opt) =>
                      editTeam.assignedWorkOrders.includes(opt.value),
                    )}
                    onChange={handleEditWorkOrderChange}
                    placeholder={
                      editTeam.assignedProjects.length > 0
                        ? "Select Work Orders for this project"
                        : "Select Project first"
                    }
                  />
                </div>

                <div className="mb-3">
                  <label>Role *</label>
                  <select
                    name="role"
                    className="form-select"
                    value={editTeam.role}
                    onChange={(e) => handleChange(e, true)}
                  >
                    <option value="">Select Role</option>
                    {roles.map((r, idx) => (
                      <option key={idx} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label>Status</label>
                  <select
                    name="status"
                    className="form-select"
                    value={editTeam.status}
                    onChange={(e) => handleChange(e, true)}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
            <button
              className="btn btn-danger"
              onClick={() => setEditModal(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </Fragment>
  );
};

export default TeamMobilization;
